import { useEffect, useId, useRef, useState } from "react";
import { FirebaseFunctionDefs } from "breadcommon";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { RyeSpinner } from "../rye/RyeSpinner";
import { RyeInput } from "../rye/RyeInput";
import { RyeModalContent } from "../rye/RyeModal";
import { buildClasses } from "../utils/buildClasses";

type BreadCancellationQuote =
  FirebaseFunctionDefs["CancelSubscription"]["requestType"]["confirmedQuote"];

export function CancelSubscriptionModal({
  onCompleted,
}: {
  onCompleted: () => void;
}): JSX.Element {
  const [quote, setQuote] = useState<BreadCancellationQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const confirmationId = useId();
  const [confirmation, setConfirmation] = useState("");
  const confirmed = confirmation === "delete";

  async function loadQuote() {
    setLoading(true);
    setQuote(null);
    setConfirmation("");
    setError(null);
    try {
      const { data } = await callFirebaseFunction("GetCancellationQuote", {});
      if (!data.success || !data.quote) {
        throw new Error(data.error_msg || "Unable to load your refund amount.");
      }
      setQuote(data.quote);
      setPending(data.cancellationPending ?? false);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Unable to load your refund amount.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadQuote();
  }, []);

  async function confirm() {
    if (!quote || !confirmed || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await callFirebaseFunction(
        "CancelSubscription",
        { confirmAccountDeletion: true, confirmedQuote: quote },
        // Allow the two-minute server request time to return its result.
        { timeout: 150_000 },
      );
      if (!data.success) {
        throw new Error(
          data.error_msg ||
            "Cancellation could not finish. Refresh your refund preview to retry.",
        );
      }
      setCompleted(true);
    } catch (error) {
      // An interrupted response may still have canceled/refunded. Require a new
      // preview so the next attempt uses the server's saved progress, if any.
      setQuote(null);
      setError(
        error instanceof Error
          ? error.message
          : "Cancellation could not finish. Refresh your refund preview to retry.",
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const currencyFormat = quote
    ? new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: quote.currency,
      })
    : null;
  const refund =
    quote && currencyFormat
      ? currencyFormat.format(
          quote.refundAmount /
            10 ** (currencyFormat.resolvedOptions().maximumFractionDigits ?? 2),
        )
      : "";

  return (
    <div className={buildClasses("w-[32rem]", "max-w-[calc(100vw-5.5rem)]")}>
      <RyeModalContent
        animateHeight
        title={
          completed
            ? "Account deleted"
            : submitting
              ? undefined
              : pending
                ? "Finish cancellation"
                : "Cancel subscription and delete account?"
        }
        showCloseX={!submitting && !completed}
        actionButtons={
          completed
            ? [
                {
                  text: "Done",
                  action: onCompleted,
                },
              ]
            : submitting
              ? []
              : [
                  {
                    text: "Never mind",
                    vibe: "subdued",
                    disabled: submitting,
                    action: () => {},
                  },
                  ...(quote || loading
                    ? [
                        {
                          text: submitting
                            ? "Canceling…"
                            : pending
                              ? "Finish cancellation"
                              : "Cancel subscription and delete account",
                          vibe: "danger" as const,
                          variant: "filled" as const,
                          close: false,
                          disabled: loading || submitting || !confirmed,
                          action: () => void confirm(),
                        },
                      ]
                    : error && !loading
                      ? [
                          {
                            text: "Try again",
                            close: false,
                            disabled: loading || submitting,
                            action: () => void loadQuote(),
                          },
                        ]
                      : []),
                ]
        }
        content={
          completed ? (
            <p>
              Your subscription is canceled and your account data has been
              deleted.
            </p>
          ) : submitting ? (
            <div
              role="status"
              className={buildClasses(
                "flex",
                "items-center",
                "justify-center",
                "gap-3",
                "py-3",
              )}
            >
              <RyeSpinner />
              <p className={buildClasses("font-medium", "text-on-surface-800")}>
                Cancelling subscription and deleting account
              </p>
            </div>
          ) : (
            <div className={buildClasses("w-full", "space-y-4", "text-sm")}>
              {loading && (
                <p role="status" className={buildClasses("sr-only")}>
                  Loading your refund amount…
                </p>
              )}
              <>
                {quote && pending && (
                  <p>
                    Your cancellation has started. Continue to finish the
                    remaining steps.
                  </p>
                )}
                {(quote || loading) && (
                  <div
                    aria-busy={loading}
                    className={buildClasses(
                      "rounded-xl",
                      "border",
                      "border-on-surface-200",
                      "bg-white",
                      "p-5",
                    )}
                  >
                    <dl
                      className={buildClasses(
                        "border-b",
                        "border-on-surface-100",
                        "pb-3",
                        "mb-4",
                        "flex",
                        "justify-between",
                        "gap-4",
                      )}
                    >
                      <dt className={buildClasses("text-on-surface-500")}>
                        Full unused months
                      </dt>
                      <dd
                        className={buildClasses(
                          "flex",
                          "h-5",
                          "items-center",
                          "justify-end",
                          "leading-5",
                          "font-medium",
                          "text-on-surface-900",
                        )}
                      >
                        {quote ? (
                          quote.unusedMonths
                        ) : (
                          <RyeSkeleton className={buildClasses("w-6", "h-5")} />
                        )}
                      </dd>
                    </dl>
                    <div
                      className={buildClasses(
                        "flex",
                        "items-center",
                        "justify-between",
                        "gap-4",
                      )}
                    >
                      <p
                        className={buildClasses(
                          "text-on-surface-500",
                          "font-medium",
                        )}
                      >
                        {pending ? "Confirmed refund" : "Your refund"}
                      </p>
                      <p
                        className={buildClasses(
                          "flex",
                          "h-9",
                          "items-center",
                          "justify-end",
                          "whitespace-nowrap",
                          "text-right",
                          "text-3xl",
                          "font-semibold",
                          "tracking-tight",
                          "text-on-surface-900",
                          "tabular-nums",
                        )}
                      >
                        {quote ? (
                          <span
                            className={buildClasses("text-3xl", "leading-9")}
                          >
                            {refund}
                            <span
                              className={buildClasses(
                                "ml-2",
                                "text-xs",
                                "font-medium",
                                "text-on-surface-500",
                                "tracking-normal",
                              )}
                            >
                              {quote.currency.toUpperCase()}
                            </span>
                          </span>
                        ) : (
                          <RyeSkeleton
                            className={buildClasses("w-28", "h-9")}
                          />
                        )}
                      </p>
                    </div>

                    {quote && (pending || quote.refundAmount === 0) && (
                      <p
                        className={buildClasses(
                          "mt-3",
                          "text-xs",
                          "text-on-surface-500",
                        )}
                      >
                        {pending
                          ? "Any refund already completed will not be issued again."
                          : "There is no payment eligible for a refund."}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <p
                    className={buildClasses(
                      "font-medium",
                      "text-on-surface-900",
                    )}
                  >
                    What happens next
                  </p>
                  <ul
                    className={buildClasses(
                      "mt-2",
                      "list-disc",
                      "pl-5",
                      "space-y-1",
                      "text-on-surface-600",
                    )}
                  >
                    {!quote ? (
                      <li>Any eligible full unused months are refunded.</li>
                    ) : (
                      quote.refundAmount > 0 && (
                        <li>
                          Your {quote.unusedMonths} unused{" "}
                          {quote.unusedMonths === 1 ? "month is" : "months are"}{" "}
                          refunded.
                        </li>
                      )
                    )}
                    <li>
                      Your subscription and access to Bread end immediately.
                    </li>
                    <li>Your bank connections are unlinked.</li>
                    <li>
                      Your account and all associated data are permanently
                      deleted.{" "}
                      <strong className={buildClasses("font-bold")}>
                        Account deletion cannot be undone.
                      </strong>
                    </li>
                  </ul>
                </div>
                <div
                  className={buildClasses(
                    "border-t",
                    "border-on-surface-200",
                    "pt-4",
                  )}
                >
                  <label
                    htmlFor={confirmationId}
                    className={buildClasses(
                      "block",
                      "mb-2",
                      "text-on-surface-700",
                    )}
                  >
                    Type{" "}
                    <code
                      className={buildClasses(
                        "font-mono",
                        "rounded",
                        "bg-on-surface-100",
                        "px-1.5",
                        "py-0.5",
                      )}
                    >
                      delete
                    </code>{" "}
                    to confirm
                  </label>
                  <RyeInput
                    id={confirmationId}
                    startingText={confirmation}
                    onChange={setConfirmation}
                    disabled={!quote || loading || submitting}
                    autoComplete="off"
                    spellCheck={false}
                    width="full"
                  />
                </div>
              </>
              {error && (
                <p role="alert" className={buildClasses("text-red-800")}>
                  {error}
                </p>
              )}
            </div>
          )
        }
      />
    </div>
  );
}
